import React, { useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import './Signup.css';
import loginImage from '../assests/login.png';
import logo from '../assests/logo.png';
import { supabase } from './SupabaseClient';

function Signup() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Password validation
  const validatePassword = () => {
    if (formData.password.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!/[A-Z]/.test(formData.password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/[0-9]/.test(formData.password)) {
      return "Password must contain at least one number";
    }
    if (!/[!@#$%^&*]/.test(formData.password)) {
      return "Password must contain at least one special character";
    }
    if (formData.password !== formData.confirmPassword) {
      return "Passwords do not match";
    }
    return null;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    // Validate password
    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      // Sign up the user with Supabase
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) throw authError;

      // Insert role into user_roles table
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert([{ user_id: authData.user.id, role: 'lead' }]);

      if (roleError) throw roleError;

      setSuccessMessage('Signup successful! You can now log in.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      console.error('Error during signup:', error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  return (
    <div className="login-container">
      <div className="company-logo">
        <img src={logo} alt="Company Logo" />
      </div>
      
      <div className="login-form-container">
        <div className="login-form-wrapper">
          <h1 className="login">Create Account</h1>
          <p className="login-subtitle">Please fill out the form to sign up</p>
          
          <form onSubmit={handleSignup}>
            <div className="form-group">
              <label1 htmlFor="email">Email</label1>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required
              />
            </div>
            
            <div className="form-group">
              <label1 htmlFor="password">Password</label1>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  required
                />
                <button 
                  type="button" 
                  className="password-toggle-btn"
                  onClick={() => togglePasswordVisibility('password')}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <i className="password-eye-icon">👁️</i>
                  ) : (
                    <i className="password-eye-icon">👁️‍🗨️</i>
                  )}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label1 htmlFor="confirmPassword">Confirm Password</label1>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  required
                />
                <button 
                  type="button" 
                  className="password-toggle-btn"
                  onClick={() => togglePasswordVisibility('confirm')}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <i className="password-eye-icon">👁️</i>
                  ) : (
                    <i className="password-eye-icon">👁️‍🗨️</i>
                  )}
                </button>
              </div>
            </div>

            <div className="password-requirements">
              <p>Password must contain:</p>
              <ul>
                <li>At least 8 characters</li>
                <li>At least one uppercase letter</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>
            
            <button 
              type="submit" 
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Signing up...' : 'Sign Up'}
            </button>
            
            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}
          </form>

          <div className="login-link">
            <p>Already have an account? <NavLink to="/login">Log In</NavLink></p>
          </div>
        </div>
      </div>
      
      <div className="login-image-container">
        <img src={loginImage} alt="Sign Up" className="login-image" />
      </div>
    </div>
  );
}

export default Signup;
